// generated by the plop

import { Inject, Injectable } from '@angular/core';
import { Action, NgxsOnInit, Selector, State, StateContext, Store } from '@ngxs/store';
import { append, patch, updateItem } from '@ngxs/store/operators';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { ProfileState } from 'shared';
import { PROGRESS_STATUSES } from 'symbols';
import { GpAsteriskService } from '../../services/gp-asterisk.service';
import {
  CallingState,
  CONNECT_STATUS_WS,
  ExtensionStatusEnum,
  GP_ASTERISK_CONFIG_TOKEN,
  GpAsteriskConfigModel,
  TypeCall
} from '../../symbols';
import {
  CallingIn,
  ChangeConnectARI,
  CreateContact,
  DongleStatus,
  ExtensionStatus,
  GetCallLogs,
  GetCallLogsFail,
  GetCallLogsSuccess,
  GetChannels,
  GetContacts,
  GetContactsFail,
  GetContactsSuccess,
  GetEndpoints,
  GetEndpointsFail,
  GetEndpointsSuccess,
  GetFavoriteContacts,
  GetFavoriteContactsFail,
  GetFavoriteContactsSuccess,
  RemoveCall,
  SetCountConnected,
  SetFavoriteContact,
  SetRoutine,
  SetVolume,
  ToCall,
  ToCallFail,
  UpdateCallState,
  UpdateContact,
  DeleteContact,
  InternalCallUpdate,
  UpdateContactFail,
  UpdateContactSuccess
} from './gp-asterisk.actions';
import { GP_ASTERISK_STATE_DEFAULT, GpAsteriskStateModel } from './gp-asterisk.model';

@State({ name: 'GpAsterisk', defaults: GP_ASTERISK_STATE_DEFAULT })
@Injectable()
export class GpAsteriskState implements NgxsOnInit {
  ws: ReconnectingWebSocket;

  constructor(@Inject(GP_ASTERISK_CONFIG_TOKEN) private config: GpAsteriskConfigModel, private gpAsteriskService: GpAsteriskService, private store: Store) {
  }

  ngxsOnInit(ctx: StateContext<any>) {
    this.connectWs(ctx);
    const volume = localStorage.getItem('volume');

    if (volume) {
      ctx.patchState({
        volume: +volume
      });
    }
  }

  @Selector()
  static updateContactStatus(state: GpAsteriskStateModel): GpAsteriskStateModel['updateContactStatus'] {
    return state.updateContactStatus;
  }

  @Selector()
  static dongleStatus(state: GpAsteriskStateModel): GpAsteriskStateModel['dongleStatus'] {
    return state.dongleStatus;
  }

  @Selector()
  static setFavoriteStatusDict(state: GpAsteriskStateModel): GpAsteriskStateModel['setFavoriteStatusDict'] {
    return state.setFavoriteStatusDict;
  }

  @Selector()
  static loadCallLogsStatus(state: GpAsteriskStateModel): GpAsteriskStateModel['loadCallLogsStatus'] {
    return state.loadCallLogsStatus;
  }

  @Selector()
  static callLogs(state: GpAsteriskStateModel): GpAsteriskStateModel['callLogs'] {
    return state.callLogs;
  }

  @Selector()
  static favoriteContacts(state: GpAsteriskStateModel): GpAsteriskStateModel['contacts'] {
    return state.favoriteContacts;
  }

  @Selector()
  static contacts(state: GpAsteriskStateModel): GpAsteriskStateModel['contacts'] {
    return state.contacts;
  }

  @Selector()
  static loadContactsStatus(state: GpAsteriskStateModel): GpAsteriskStateModel['loadContactsStatus'] {
    return state.loadContactsStatus;
  }

  @Selector()
  static isWorkTime(state: GpAsteriskStateModel): GpAsteriskStateModel['isWorkTime'] {
    return state.isWorkTime;
  }

  @Selector()
  static callList(state: GpAsteriskStateModel): GpAsteriskStateModel['callList'] {
    return state.callList;
  }

  @Selector()
  static connectStatus(state: GpAsteriskStateModel): (CONNECT_STATUS_WS | ExtensionStatusEnum) {
    if (state.connectStatus === CONNECT_STATUS_WS.CONNECTED) {
      return state.statusExtension;
    } else {
      return state.connectStatus;
    }
  }

  @Selector()
  static isReadyForCall(state: GpAsteriskStateModel): boolean {

    return state.connectStatus === CONNECT_STATUS_WS.CONNECTED &&
      (
        state.statusExtension === ExtensionStatusEnum.ONLINE ||
        state.statusExtension === ExtensionStatusEnum.IDLE ||
        state.statusExtension === ExtensionStatusEnum.REACHABLE ||
        state.statusExtension === ExtensionStatusEnum.REGISTERED
      );
  }

  @Selector()
  static volumeRinging(state: GpAsteriskStateModel): GpAsteriskStateModel['volume'] {
    return state.volume;
  }


  @Selector()
  static countConnected(state: GpAsteriskStateModel): GpAsteriskStateModel['countConnected'] {
    return state.countConnected;
  }

  @Selector()
  static toCallStatus(state: GpAsteriskStateModel): GpAsteriskStateModel['toCallStatus'] {
    return state.toCallStatus;
  }

  @Selector()
  static loadEndpointStatus(state: GpAsteriskStateModel): GpAsteriskStateModel['loadEndpointStatus'] {
    return state.loadEndpointStatus;
  }


  @Selector()
  static endpoints(state: GpAsteriskStateModel): GpAsteriskStateModel['endpoints'] {
    return state.endpoints;
  }

  @Action(ChangeConnectARI)
  changeConnectARI(ctx: StateContext<GpAsteriskStateModel>, { payload }: ChangeConnectARI): void {
    ctx.patchState({
      connectStatus: payload,
      callList: payload === CONNECT_STATUS_WS.CONNECTED ? ctx.getState().callList : []
    });
  }

  @Action(CallingIn)
  callingIn(ctx: StateContext<GpAsteriskStateModel>, { payload }: CallingIn): void {
    ctx.patchState({ callList: [...ctx.getState().callList, payload] });
    this.syncRing(ctx);
  }

  // Дзвінок у CRM звучить, доки є вхідна розмова, яку ще ніхто не взяв
  // (очікування або телефон оператора ще дзвонить), і замовкає одразу після відповіді/завершення
  private syncRing(ctx: StateContext<GpAsteriskStateModel>): void {
    const hasUnanswered = ctx.getState().callList
      .some(channel => channel.type === TypeCall.InCall && channel.status !== CallingState.Answered);
    if (hasUnanswered) {
      // поки ваш телефон у розмові — дзвінок у CRM беззвучний (див. ExtensionStatus)
      const isBusy = ctx.getState().statusExtension === ExtensionStatusEnum.IN_USE;
      this.gpAsteriskService.playRing(isBusy ? 0 : ctx.getState().volume);
    } else {
      this.gpAsteriskService.stopRing();
    }
  }

  @Action(RemoveCall)
  removeCall(ctx: StateContext<GpAsteriskStateModel>, { payload }: RemoveCall): void {

    ctx.patchState({ callList: [...ctx.getState().callList].filter(item => item.id !== payload) });
    this.syncRing(ctx);
  }

  @Action(GetChannels)
  getChannels(ctx: StateContext<GpAsteriskStateModel>, { callList }: GetChannels): void {
    ctx.patchState({
      callList: ctx.getState().callList.length ? ctx.getState().callList : callList
    });
  }


  @Action(UpdateCallState)
  updateState(ctx: StateContext<GpAsteriskStateModel>, { payload }: UpdateCallState): void {

    ctx.setState(patch<GpAsteriskStateModel>(
      {
        callList: updateItem(item => item.id === payload.id, patch({
          answered: payload.answered,
          isHold: payload.isHold,
          isDisabled: payload.isDisabled,
          nameCaller: payload.nameCaller,
          status: payload.status,
          // хто веде розмову, переадресація, конференція — потрібні для кнопок у картці дзвінка
          SIP_number: payload.SIP_number,
          dialedId: payload.dialedId,
          isDial: payload.isDial,
          transferredFrom: payload.transferredFrom,
          conferenceMembers: payload.conferenceMembers,
          transfer: payload.transfer
        }))
      }
    ));
    this.syncRing(ctx);

  }

  @Action(SetVolume)
  async setVolume(ctx: StateContext<GpAsteriskStateModel>, { value }: SetVolume) {
    this.gpAsteriskService.stopRing();
    ctx.patchState({ volume: value });
    localStorage.setItem('volume', `${value}`);
    const audio = new Audio('/shared/src/assets/sounds/calling.mp3');
    audio.volume = value;
    await audio.play();
  }

  @Action(SetCountConnected)
  setCountConnected(ctx: StateContext<GpAsteriskStateModel>, { count }: SetCountConnected): void {
    ctx.patchState({ countConnected: count });
  }

  @Action(ToCall)
  toCall(ctx: StateContext<GpAsteriskStateModel>, { tel, endpoint }: ToCall): void {
    ctx.patchState({ toCallStatus: PROGRESS_STATUSES.IN_PROGRESS });
    this.gpAsteriskService.toCall(tel, endpoint).subscribe({
      next: () => ctx.patchState({ toCallStatus: PROGRESS_STATUSES.SUCCESS }),
      error: () => ctx.dispatch(new ToCallFail())
    });
  }


  @Action(ToCallFail)
  toCallFail(ctx: StateContext<GpAsteriskStateModel>): void {
    ctx.patchState({ toCallStatus: PROGRESS_STATUSES.FAIL });
    console.error(ToCallFail);
  }

  @Action(GetEndpoints)
  getEndpoints(ctx: StateContext<GpAsteriskStateModel>): void {
    ctx.patchState({
      loadEndpointStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.gpAsteriskService.getEndpoints().subscribe({
      next: res => ctx.dispatch(new GetEndpointsSuccess(res)),
      error: err => ctx.dispatch(new GetEndpointsFail(err))
    });
  }

  @Action(GetEndpointsSuccess)
  GetEndpointsSuccess(ctx: StateContext<GpAsteriskStateModel>, { endpoints }: GetEndpointsSuccess): void {
    ctx.patchState({
      loadEndpointStatus: PROGRESS_STATUSES.SUCCESS,
      endpoints
    });
  }

  @Action(GetEndpointsFail)
  getEndpointsFail(ctx: StateContext<GpAsteriskStateModel>, { error }: GetEndpointsFail): void {
    ctx.patchState({
      loadEndpointStatus: PROGRESS_STATUSES.FAIL
    });
  }

  @Action(ExtensionStatus)
  extensionStatus(ctx: StateContext<GpAsteriskStateModel>, { payload }: ExtensionStatus): void {
    const user = this.store.selectSnapshot(ProfileState.currentUser);
    if (payload.exten === user?.code) {

      if (payload.statusText === ExtensionStatusEnum.IN_USE) {
        this.gpAsteriskService.audio.volume = 0;
      } else {
        this.gpAsteriskService.audio.volume = this.store.selectSnapshot(GpAsteriskState.volumeRinging);
      }


      ctx.patchState({
        statusExtension: payload.statusText
      });
    }
  }

  @Action(SetRoutine)
  setRoutine(ctx: StateContext<GpAsteriskStateModel>, { payload }: SetRoutine): void {
    ctx.patchState({ isWorkTime: payload });
  }


  private connectWs(ctx: StateContext<GpAsteriskStateModel>) {
    let protocol = 'ws://';
    if (window.location.protocol === 'https:') {
      protocol = 'wss://';
    }

    if (this.config.wsBaseUrl) {
      this.ws = new ReconnectingWebSocket(`${this.config.wsBaseUrl}/ws-asterisk`, [], { connectionTimeout: 7000 });
    } else if (window.location.hostname === 'localhost') {
      this.ws = new ReconnectingWebSocket(
        `${protocol}${window.location.hostname}:${this.config.wsPort}/ws-asterisk`,
        [],
        { connectionTimeout: 7000 }
      );
    } else {
      this.ws = new ReconnectingWebSocket(
        `${protocol}${window.location.host}/ws-asterisk`,
        [],
        { connectionTimeout: 7000 }
      );
    }


    this.ws.onclose = () => {
      ctx.dispatch(new ChangeConnectARI(CONNECT_STATUS_WS.DISCONNECT_BACKEND));
    };
    this.ws.onmessage = (res) => {
      const data: { type: string, payload: any } = JSON.parse(res.data);
      switch (data.type) {
        case '[Asterisk] SetCountConnections': {
          ctx.dispatch(new SetCountConnected(data.payload));
          break;
        }
        case '[Asterisk] ChangeConnectARI': {
          ctx.dispatch(new ChangeConnectARI(data.payload));
          break;
        }
        case '[Asterisk] GetChannels': {
          ctx.dispatch(new GetChannels(data.payload));
          break;
        }
        case '[Asterisk] CallingIn': {
          ctx.dispatch(new CallingIn(data.payload));
          break;
        }
        case '[Asterisk] RemoveCall': {
          ctx.dispatch(new RemoveCall(data.payload));
          break;
        }
        case '[Asterisk] UpdateState': {
          ctx.dispatch(new UpdateCallState(data.payload));
          break;
        }
        case '[Asterisk] Extension Status': {
          ctx.dispatch(new ExtensionStatus(data.payload));
          break;
        }
        case '[Asterisk] SetRoutine': {
          ctx.dispatch(new SetRoutine(data.payload));
          break;
        }
        case '[Asterisk] DongleStatus': {
          ctx.dispatch(new DongleStatus(data.payload));
          if (data.payload.device?.startsWith('SIP/')) {
            this.gpAsteriskService.emitPeerStatus(data.payload.device, data.payload.status);
          }
          break;
        }
        case '[Asterisk] PeerStatus': {
          this.gpAsteriskService.emitPeerStatus(data.payload.device, data.payload.status);
          break;
        }
        case '[Asterisk] DirectCallStatus': {
          this.gpAsteriskService.setDirectCallsStatus(data.payload);
          break;
        }
        // внутрішній дзвінок / дзвінок клієнту через застосунок: стан слухає mobile (InternalCallService)
        case '[Asterisk] InternalCall': {
          ctx.dispatch(new InternalCallUpdate(data.payload));
          break;
        }
        default:
          console.log(data);
      }
    };
  }

// added actions by plop
  @Action(GetContacts)
  getContacts(ctx: StateContext<GpAsteriskStateModel>, { search }: GetContacts): void {
    ctx.patchState({
      loadContactsStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.gpAsteriskService.getContacts(null, search).subscribe({
      next: res => ctx.dispatch(new GetContactsSuccess(res)),
      error: err => ctx.dispatch(new GetContactsFail(err))
    });
  }

  @Action(GetContactsSuccess)
  getContactsSuccess(ctx: StateContext<GpAsteriskStateModel>, { contacts }: GetContactsSuccess): void {
    ctx.patchState({
      contacts,
      loadContactsStatus: PROGRESS_STATUSES.SUCCESS
    });
  }

  @Action(GetContactsFail)
  getContactsFail(ctx: StateContext<GpAsteriskStateModel>, { error }: GetContactsFail): void {
    console.error(error);
    ctx.patchState({
      loadContactsStatus: PROGRESS_STATUSES.FAIL
    });
  }

// added actions by plop
  @Action(GetFavoriteContacts)
  getFavoriteContacts(ctx: StateContext<GpAsteriskStateModel>): void {
    ctx.patchState({
      loadContactsStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.gpAsteriskService.getContacts(true).subscribe({
      next: res => ctx.dispatch(new GetFavoriteContactsSuccess(res)),
      error: err => ctx.dispatch(new GetFavoriteContactsFail(err))
    });
  }

  @Action(GetFavoriteContactsSuccess)
  getFavoriteContactsSuccess(ctx: StateContext<GpAsteriskStateModel>, { contacts }: GetFavoriteContactsSuccess): void {
    ctx.patchState({
      favoriteContacts: contacts,
      loadContactsStatus: PROGRESS_STATUSES.SUCCESS
    });
  }

  @Action(GetFavoriteContactsFail)
  getFavoriteContactsFail(ctx: StateContext<GpAsteriskStateModel>, { error }: GetFavoriteContactsFail): void {
    console.error(error);
    ctx.patchState({
      loadContactsStatus: PROGRESS_STATUSES.FAIL
    });
  }


// added actions by plop
  @Action(GetCallLogs)
  getCallLogs(ctx: StateContext<GpAsteriskStateModel>, { filter, lazy }: GetCallLogs): void {
    if (lazy) {
      const next = ctx.getState().callLogs?.links?.next;
      if (next) {
        ctx.patchState({
          loadCallLogsStatus: PROGRESS_STATUSES.IN_PROGRESS
        });
        this.gpAsteriskService.getCallLogsLazy(next).subscribe({
          next: orders => ctx.dispatch(new GetCallLogsSuccess(orders, lazy)),
          error: error => ctx.dispatch(new GetCallLogsFail(error))
        });
      }
    } else {
      ctx.patchState({
        loadCallLogsStatus: PROGRESS_STATUSES.IN_PROGRESS
      });
      this.gpAsteriskService.getCallLogs(filter).subscribe({
        next: orders => ctx.dispatch(new GetCallLogsSuccess(orders, lazy)),
        error: error => ctx.dispatch(new GetCallLogsFail(error))
      });
    }
  }

  @Action(GetCallLogsSuccess)
  getCallLogsSuccess(ctx: StateContext<GpAsteriskStateModel>, { callLogs, lazy }: GetCallLogsSuccess): void {
    ctx.patchState({
      loadCallLogsStatus: PROGRESS_STATUSES.SUCCESS
    });
    if (lazy) {
      ctx.setState(patch({
        callLogs: patch({
          data: append([...callLogs.data]),
          links: callLogs.links,
          meta: callLogs.meta
        })
      }));
    } else {
      ctx.patchState({
        callLogs
      });

    }
  }

  @Action(GetCallLogsFail)
  getCallLogsFail(ctx: StateContext<GpAsteriskStateModel>, { error }: GetCallLogsFail): void {
    console.error(error);
    ctx.patchState({
      loadCallLogsStatus: PROGRESS_STATUSES.FAIL
    });
  }

  @Action(SetFavoriteContact)
  setFavoriteContact(ctx: StateContext<GpAsteriskStateModel>, { isFavorite, contactId }: SetFavoriteContact): void {
    const statuses = ctx.getState().setFavoriteStatusDict;
    ctx.patchState({
      setFavoriteStatusDict: { ...statuses, [contactId]: PROGRESS_STATUSES.IN_PROGRESS }
    });

    this.gpAsteriskService.setFavorite(contactId, isFavorite).subscribe({
      next: res => {
        ctx.patchState({
          setFavoriteStatusDict: { ...statuses, [contactId]: PROGRESS_STATUSES.SUCCESS }
        });

        ctx.setState(patch({
          contacts: updateItem(c => c.id === contactId, patch({
            isFavorite
          }))
        }));

        ctx.setState(patch({
          favoriteContacts: updateItem(c => c.id === contactId, patch({
            isFavorite
          }))
        }));
      },
      error: err => {
        ctx.patchState({
          setFavoriteStatusDict: { ...statuses, [contactId]: PROGRESS_STATUSES.FAIL }
        });
      }
    });
  }

  @Action(DongleStatus)
  dongleStatus(ctx: StateContext<GpAsteriskStateModel>, { payload }: DongleStatus) {
    // PJSIP/<номер> — вбудований телефон у застосунку (старий бекенд теж слав його як «модем»)
    if (payload.device?.startsWith('PJSIP/')) return;
    ctx.patchState({
      dongleStatus: [payload, ...ctx.getState().dongleStatus.filter(d => d.device !== payload.device)]
    });
  }


  @Action(CreateContact)
  createContact(ctx: StateContext<GpAsteriskStateModel>, { contact }: CreateContact): void {
    ctx.patchState({
      updateContactStatus: PROGRESS_STATUSES.IN_PROGRESS
    });

    this.gpAsteriskService.createContact(contact).subscribe({
      next: res => {
        ctx.setState(patch({
          contacts: [...ctx.getState().contacts, res]
        }));
        ctx.dispatch(new UpdateContactSuccess());
      },
      error: err => {
        console.error(err);
        ctx.dispatch(new UpdateContactFail(err));
      }
    });
  }

  @Action(UpdateContact)
  updateContact(ctx: StateContext<GpAsteriskStateModel>, { contact }: UpdateContact): void {
    ctx.patchState({
      updateContactStatus: PROGRESS_STATUSES.IN_PROGRESS
    });

    this.gpAsteriskService.updateContact(contact).subscribe({
      next: res => {
        ctx.setState(patch({
          contacts: updateItem(c => c.id === res.id, res),
          favoriteContacts: updateItem(c => c.id === res.id, res)
        }));
        ctx.dispatch(new UpdateContactSuccess());
      },
      error: err => {
        console.error(err);
        ctx.dispatch(new UpdateContactFail(err));
      }
    });
  }

  @Action(DeleteContact)
  deleteContact(ctx: StateContext<GpAsteriskStateModel>, { id }: DeleteContact): void {
    ctx.patchState({ updateContactStatus: PROGRESS_STATUSES.IN_PROGRESS });
    this.gpAsteriskService.deleteContact(id).subscribe({
      next: () => {
        ctx.patchState({
          contacts: ctx.getState().contacts.filter(c => c.id !== id),
          favoriteContacts: ctx.getState().favoriteContacts.filter(c => c.id !== id)
        });
        ctx.dispatch(new UpdateContactSuccess());
      },
      error: err => ctx.dispatch(new UpdateContactFail(err))
    });
  }

  @Action(UpdateContactFail)
  updateContactFail(ctx: StateContext<GpAsteriskStateModel>, { error }: UpdateContactFail): void {
    console.error(error);
    ctx.patchState({
      updateContactStatus: PROGRESS_STATUSES.FAIL
    });
  }

  @Action(UpdateContactSuccess)
  updateContactSuccess(ctx: StateContext<GpAsteriskStateModel>): void {
    ctx.patchState({
      updateContactStatus: PROGRESS_STATUSES.SUCCESS
    });
  }
}


